/**
 * Jubly Account Page
 * Services, billing, contributions, profile, and business settings
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import {
  Mail, Calendar, Phone, Video, Shield, CreditCard, Users, Building2,
  ChevronRight, Check, X, Settings, Globe, ExternalLink, ToggleLeft, ToggleRight
} from 'lucide-react';

function ServiceCard({ icon: Icon, name, description, enabled, href, onToggle, badge }) {
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${enabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900 text-sm">{name}</h4>
          {badge && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{badge}</span>}
        </div>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      {onToggle ? (
        <button onClick={onToggle} className="flex-shrink-0">
          {enabled ? (
            <div className="w-11 h-6 bg-blue-600 rounded-full relative">
              <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
            </div>
          ) : (
            <div className="w-11 h-6 bg-gray-200 rounded-full relative">
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
            </div>
          )}
        </button>
      ) : href ? (
        <a href={href} className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </a>
      ) : (
        <div className="flex-shrink-0">
          {enabled ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-gray-300" />}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const emailDomain = user?.emailAddress?.split('@')[1];
  const userDomain = emailDomain && emailDomain !== 'jubly.me' && emailDomain !== 'jubly.net' ? emailDomain : null;
  const [profile, setProfile] = useState(null);
  const [billing, setBilling] = useState(null);
  const [hasVoice, setHasVoice] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/contributor/profile', { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch('/api/voice/numbers', { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch('/api/voice/billing', { credentials: 'include' }).then(r => r.ok ? r.json() : null),
    ]).then(([profileData, voiceData, billingData]) => {
      setProfile(profileData?.data);
      const nums = Array.isArray(voiceData) ? voiceData : voiceData?.numbers || [];
      setHasVoice(nums.length > 0);
      setBilling(billingData);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const voiceDismissed = localStorage.getItem('jubly_voice_dismissed');

  const handleEnableVoice = () => {
    localStorage.removeItem('jubly_voice_dismissed');
    window.location.href = '/voice';
  };

  const [activeTab, setActiveTab] = useState('overview');
  const initial = (user?.displayName || user?.emailAddress || '?')[0].toUpperCase();

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'services', label: 'Services' },
    { key: 'billing', label: 'Billing' },
    { key: 'contributions', label: 'Contributions' },
    { key: 'settings', label: 'Settings' },
  ];
  if (userDomain) TABS.splice(3, 0, { key: 'business', label: 'Business' });

  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingTop: 0 }}>
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Profile Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-md">
              <span className="text-white text-2xl font-bold">{initial}</span>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{user?.displayName || 'User'}</h1>
              <p className="text-sm text-gray-500">{user?.emailAddress}</p>
              {profile?.isContributor && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                    {profile.points || 0} contribution points
                  </span>
                  <span className="text-xs text-gray-400">Level: {profile.level}</span>
                </div>
              )}
            </div>
            <a href="/account/contributions" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Contributions →
            </a>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 rounded-xl bg-gray-100 p-1 mb-6 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <>
            <Section title="Your Services">
          <ServiceCard
            icon={Mail}
            name="Mail"
            description="Email with labels, filters, signatures, and keyboard shortcuts"
            enabled={true}
            href="/email"
            badge="Active"
          />
          <ServiceCard
            icon={Calendar}
            name="Calendar"
            description="Events, scheduling, invitations, iPhone/Android sync"
            enabled={true}
            href="/calendar"
          />
          <ServiceCard
            icon={Video}
            name="Meet"
            description="Video conferencing with screen sharing and recording"
            enabled={true}
            href="/meet"
          />
          <ServiceCard
            icon={Phone}
            name="Voice & SMS"
            description={hasVoice ? 'Phone number, calls, SMS, voicemail' : 'Get a phone number for calls, SMS, and voicemail'}
            enabled={hasVoice}
            onToggle={!hasVoice || voiceDismissed ? handleEnableVoice : undefined}
            href={hasVoice ? '/voice' : undefined}
            badge={hasVoice ? 'Active' : voiceDismissed ? 'Dismissed' : 'Not set up'}
          />
            </Section>
          </>
        )}

        {/* Services tab */}
        {activeTab === 'services' && (
          <Section title="Your Services">
            <ServiceCard icon={Mail} name="Mail" description="Email with labels, filters, signatures, and keyboard shortcuts" enabled={true} href="/email" badge="Active" />
            <ServiceCard icon={Calendar} name="Calendar" description="Events, scheduling, invitations, iPhone/Android sync" enabled={true} href="/calendar" badge="Active" />
            <ServiceCard icon={Video} name="Meetings" description="Video conferencing with screen sharing and recording" enabled={true} href="/meet" />
            <ServiceCard icon={Phone} name="Voice & SMS" description="Phone number, calls, SMS, voicemail" enabled={hasVoice} onToggle={!hasVoice ? handleEnableVoice : undefined} badge={hasVoice ? 'Active' : voiceDismissed ? 'Dismissed' : 'Not set up'} />
          </Section>
        )}

        {/* Billing tab */}
        {(activeTab === 'billing' || activeTab === 'overview') && (
        <Section title="Billing">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-gray-400" />
                <h4 className="font-medium text-gray-900">Current Period</h4>
              </div>
              {billing?.summary && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">${(billing.summary.totalCost || 0).toFixed(2)}</div>
                  <div className="text-[10px] text-gray-500">{billing.period?.month || 'this month'}</div>
                </div>
              )}
            </div>

            {billing?.summary ? (
              <div className="space-y-3">
                {/* Service breakdown */}
                {[
                  {
                    label: 'Voice Calls',
                    icon: '📞',
                    detail: `${billing.summary.totalCalls || 0} calls · ${(billing.summary.inboundMinutes || 0) + (billing.summary.outboundMinutes || 0)} min`,
                    cost: billing.summary.callCost,
                    rate: `${billing.summary.rates?.voiceOutbound || '$0.0176'}/min`,
                  },
                  {
                    label: 'SMS Messages',
                    icon: '💬',
                    detail: `${billing.summary.totalSms || 0} messages (${billing.summary.smsOutbound || 0} sent, ${billing.summary.smsInbound || 0} received)`,
                    cost: billing.summary.smsCost,
                    rate: `${billing.summary.rates?.smsOutbound || '$0.0054'}/msg`,
                  },
                  {
                    label: 'Video Meetings',
                    icon: '🎥',
                    detail: `${billing.summary.meetingCount || 0} meetings · ${billing.summary.meetingMinutes || 0} min (${(billing.summary.meetingFreeMinutes || 10000).toLocaleString()} free)`,
                    cost: billing.summary.meetingCost,
                    rate: billing.summary.rates?.videoMeeting || '$0.0054/min',
                  },
                  {
                    label: 'Email',
                    icon: '✉️',
                    detail: `${billing.summary.emailsSent || 0} sent (${(billing.summary.emailFreeEmails || 3000).toLocaleString()} free/mo)`,
                    cost: billing.summary.emailCost,
                    rate: billing.summary.rates?.email || '$0.00135/email',
                  },
                  {
                    label: 'Voicemail Transcription',
                    icon: '📝',
                    detail: `${billing.summary.transcriptionCount || 0} transcriptions · ${billing.summary.transcriptionMinutes || 0} min`,
                    cost: billing.summary.transcriptionCost,
                    rate: billing.summary.rates?.transcription || '$0.0081/min',
                  },
                  {
                    label: 'Phone Numbers',
                    icon: '📱',
                    detail: `${billing.numbers?.length || 0} numbers`,
                    cost: billing.summary.numberFees,
                    rate: '$1.45/mo each',
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm">{item.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{item.label}</p>
                        <p className="text-[11px] text-gray-400">{item.detail}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className="text-sm font-semibold text-gray-900">${(item.cost || 0).toFixed(2)}</p>
                      <p className="text-[10px] text-gray-400">{item.rate}</p>
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900">Total</p>
                  <p className="text-lg font-bold text-gray-900">${(billing.summary.totalCost || 0).toFixed(2)}</p>
                </div>
                <a href="/account/billing"
                  className="mt-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                  View full billing details →
                </a>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No billing data yet</p>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-sm text-gray-600 mb-1">Pricing model: <strong>cost + 45% margin</strong></p>
            <p className="text-xs text-gray-400">⅓ profit → jubly.org · ⅓ → contributor dividends · ⅓ → future investment</p>
            <a href="/equity" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
              Learn about our equity model <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </Section>
        )}

        {/* Business tab */}
        {activeTab === 'business' && (
        <Section title="Jubly Business">
          {userDomain ? (
            <a href="/admin" className="block bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-green-500" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{userDomain}</h4>
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Business</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Manage domain, DNS, team members, and organization settings</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </a>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-3 mb-3">
                <Building2 className="w-5 h-5 text-purple-500" />
                <h4 className="font-medium text-gray-900">Custom Domain & Team</h4>
              </div>
              <p className="text-sm text-gray-500 mb-4">Get mail@yourdomain.com, manage team members, and customize your workspace.</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Globe className="w-4 h-4 text-gray-400" />
                  Custom email domain (e.g. team@yourcompany.com)
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4 text-gray-400" />
                  Add and manage team members
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Shield className="w-4 h-4 text-gray-400" />
                  Admin controls and permissions
                </div>
              </div>
              <a href="/signup" className="mt-4 inline-block px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
                Upgrade to Jubly Business
              </a>
            </div>
          )}
        </Section>
        )}

        {/* Contributions tab */}
        {(activeTab === 'contributions' || activeTab === 'overview') && (
        <Section title="Contributions & Ownership">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-amber-500" />
                <h4 className="font-medium text-gray-900">Your Ownership</h4>
              </div>
              <span className="text-lg font-bold text-amber-600">{profile?.points || 0} pts</span>
            </div>
            {profile?.isContributor ? (
              <>
                <p className="text-sm text-gray-500 mb-3">You've made {profile.proposalCount || 0} proposals and joined {profile.committeeCount || 0} committees.</p>
                <div className="flex gap-2">
                  <a href="/account/contributions" className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors">
                    View contributions
                  </a>
                  <a href="/equity" className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors">
                    Read the Constitution
                  </a>
                </div>
              </>
            ) : (
              <ContributorRegistration />
            )}
          </div>
        </Section>
        )}

        {/* Settings tab */}
        {activeTab === 'settings' && (
        <Section title="Account">
          <a href="/admin" className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
            <Settings className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 text-sm">Admin Settings</h4>
              <p className="text-xs text-gray-500">API keys, service configuration, domain management</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </a>
          <button
            onClick={async () => { await logout(); window.location.href = '/login'; }}
            className="w-full flex items-center gap-4 p-4 rounded-xl border border-red-100 bg-white hover:bg-red-50 transition-colors text-left"
          >
            <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            <span className="text-sm font-medium text-red-600">Sign out</span>
          </button>
        </Section>
        )}

      </div>
    </div>
  );
}

function ContributorRegistration() {
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedTax, setAgreedTax] = useState(false);
  const [loading, setLoading] = useState(false);

  async function register() {
    if (!agreedTerms || !agreedTax) return;
    setLoading(true);
    try {
      await fetch('/api/contributor/register', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agreed_terms: true, agreed_tax: true }),
      });
      window.location.reload();
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">Every contribution earns real ownership. Bug reports, ideas, code, support — everything counts.</p>
      <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
        <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} className="mt-0.5 rounded" />
        <span>I agree to the <a href="/terms" target="_blank" className="text-blue-600 underline">Terms & Conditions</a> and <a href="/privacy" target="_blank" className="text-blue-600 underline">Privacy Policy</a></span>
      </label>
      <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
        <input type="checkbox" checked={agreedTax} onChange={e => setAgreedTax(e.target.checked)} className="mt-0.5 rounded" />
        <span>I understand that contribution points represent potential future equity, and that I will need to provide tax information (e.g., W-9/W-8BEN) before any ownership or dividends can be distributed.</span>
      </label>
      <button
        onClick={register}
        disabled={!agreedTerms || !agreedTax || loading}
        className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
      >
        {loading ? 'Registering...' : 'Become an Official Contributor'}
      </button>
    </div>
  );
}
